import {
  InspectorOEMSConfig,
  InspectorReportConfig,
  InspectorRestConfig,
  RequestTypes,
  useInspectorContext,
} from "@app-context/inspector/context";
import { useRefCallback } from "@enfusion-ui/hooks";
import {
  Button,
  ColumnGrid,
  FormPanel,
  PanelRow,
  TextInput,
} from "@enfusion-ui/web-components";
import { errorToast, styled, useIsContentMobile } from "@enfusion-ui/web-core";
import { faKey } from "@fortawesome/free-solid-svg-icons";
import * as React from "react";

const ActionButton = styled(Button)`
  height: 38px;
  width: 140px;
  margin-left: auto;
`;

export type AuthViewProps = {
  disable?: boolean;
  onClick?: () => Promise<void>;
};
export const AuthView: React.FC<AuthViewProps> = () => {
  const {
    config: configBase,
    setConfig,
    requestType,
    authenticateWith2FA,
    requiresTwoFactorAuthentication,
  } = useInspectorContext();

  const config =
    requestType === RequestTypes.ENFUSION
      ? (configBase as InspectorRestConfig)
      : requestType === RequestTypes.REPORT
      ? (configBase as InspectorReportConfig)
      : (configBase as InspectorOEMSConfig);
  const { username, password, adminuser, adminpassword, inputToken } = config;

  const isMobile = useIsContentMobile();

  const handleUsernameChange = useRefCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setConfig({ ...config, username: e.target.value });
      if (e.target.value == null) errorToast("User name cannot be empty.");
    },
    [config]
  );

  const handlePasswordChange = useRefCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setConfig({ ...config, password: e.target.value });
    },
    [config]
  );
  const handleAdminuserChange = useRefCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setConfig({ ...config, adminuser: e.target.value });
      if (e.target.value == null)
        errorToast("Admin User name cannot be empty.");
    },
    [config]
  );
  const handleAdminPasswordChange = useRefCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setConfig({ ...config, adminpassword: e.target.value });
    },
    [config]
  );
  const handleTokenChange = useRefCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setConfig({ ...config, inputToken: e.target.value });
    },
    [config]
  );

  return (
    <form
      autoComplete="off"
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <input
        autoComplete="off"
        name="hidden"
        type="text"
        role="presentation"
        style={{ display: "none" }}
      />
      <ColumnGrid numColumns={3} gapSize="xl" isMobile={isMobile}>
        <FormPanel title="User Credentials" numColumns={1}>
          <PanelRow>
            <TextInput
              label="User"
              value={username}
              onChange={handleUsernameChange}
              disabled={requiresTwoFactorAuthentication}
              required
              clearable
              autoComplete="off"
              role="presentation"
            />
          </PanelRow>
          <PanelRow>
            <TextInput
              label="Password"
              value={password}
              disabled={requiresTwoFactorAuthentication}
              type="password"
              onChange={handlePasswordChange}
              required
              clearable
              autoComplete="new-password"
              role="presentation"
            />
          </PanelRow>
        </FormPanel>
        <FormPanel title="Admin Credentials" numColumns={1}>
          <PanelRow>
            <TextInput
              label="Admin User"
              value={adminuser}
              onChange={handleAdminuserChange}
              clearable
              autoComplete="off"
              role="presentation"
            />
          </PanelRow>
          <PanelRow>
            <TextInput
              label="Admin Password"
              value={adminpassword}
              type="password"
              onChange={handleAdminPasswordChange}
              clearable
              autoComplete="new-password"
              role="presentation"
            />
          </PanelRow>
        </FormPanel>
        <FormPanel title="2-Factor Token" numColumns={1}>
          <PanelRow>
            <TextInput
              label="Token"
              value={inputToken}
              onChange={handleTokenChange}
              disabled={!requiresTwoFactorAuthentication}
              clearable
              autoComplete="off"
              role="presentation"
            />
          </PanelRow>
          <PanelRow>
            <ActionButton
              icon={faKey}
              primary
              onClick={authenticateWith2FA}
              disabled={!requiresTwoFactorAuthentication}
            >
              Submit Token
            </ActionButton>
          </PanelRow>
        </FormPanel>
      </ColumnGrid>
    </form>
  );
};
